import { motion } from 'framer-motion'
import { AlertTriangle, Clock, Calculator, FileWarning, ArrowDown } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useAnimateOnScroll } from '@/hooks/useAnimateOnScroll'

const problems = [
  { icon: Calculator, color: 'text-loss-400' },
  { icon: FileWarning, color: 'text-loss-400' },
  { icon: Clock, color: 'text-loss-400' },
  { icon: AlertTriangle, color: 'text-loss-400' },
]

export function Pain() {
  const { t } = useLanguage()
  const { ref, isVisible } = useAnimateOnScroll({ threshold: 0.2 })

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-midnight-950 via-midnight-900 to-midnight-950" />

      {/* Red Glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-slate-100"
          >
            {t.pain.headline}
          </motion.h2>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl sm:text-2xl text-loss-400 font-medium mb-16"
          >
            {t.pain.subheadline}
          </motion.p>

          {/* Problem Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {t.pain.problems.map((problem, index) => {
              const IconComponent = problems[index].icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  animate={isVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="glass-card p-6 text-left hover:border-loss-500/30 transition-colors group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-loss-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-loss-500/20 transition-colors">
                      <IconComponent className={`w-6 h-6 ${problems[index].color}`} />
                    </div>
                    <div>
                      <p className="text-slate-300 text-lg">{problem}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Solution Teaser */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col items-center"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="mb-4"
            >
              <ArrowDown className="w-8 h-8 text-gold-400" />
            </motion.div>
            <p className="text-2xl font-semibold gradient-text">{t.pain.solution}</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
